import { useState } from 'react';
import { Link } from 'react-router';
import { TREND_METRICS } from '@crm/shared/contracts';
import { Async } from '../../components/Async.tsx';
import { BarList } from '../../components/charts/BarList.tsx';
import { TrendChart } from '../../components/charts/TrendChart.tsx';
import { ImmatureCohortNotice } from '../../components/ImmatureCohortNotice.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Skeleton } from '../../components/ui/skeleton.tsx';
import { formatDate, formatNumber, formatPercent, formatSeconds } from '../../lib/format.ts';
import { share } from '../../lib/home.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import {
  useCallPatterns,
  useCallSummary,
  useFunnel,
  useMarketing,
  useMe,
  useOverview,
  useTrend,
} from '../../lib/queries.ts';
import { carryOver, useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';
import { AdsSection } from '../home/AdsSection.tsx';
import { CallHeatmap } from '../home/CallHeatmap.tsx';
import { HomeFunnel } from '../home/HomeFunnel.tsx';
import { HourlyCallsChart } from '../home/HourlyCallsChart.tsx';
import { HomeSection, StatStrip, StatTile } from '../home/StatTile.tsx';

/**
 * The home page (docs/DASHBOARD_SPECIFICATION.md §3.1): what the owner checks first, in three
 * stories — sales, calls, advertising — each a strip of key figures followed by the charts that
 * explain them. Every widget loads and fails on its own.
 */
export function OverviewPage() {
  const t = useT();
  usePageTitle(t.overview.title);
  const [params] = useDashboardParams();
  const me = useMe();
  const overview = useOverview(params);
  const canMarketing = me.data?.permissions.includes('dashboard.marketing') ?? false;
  const showDelta = params.compare !== 'none';

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h1 className="text-2xl font-bold tracking-tight">{t.overview.title}</h1>
        <Controls meta={overview.data?.meta} />
      </div>
      <SalesSection showDelta={showDelta} withSources={canMarketing} />
      <CallsSection showDelta={showDelta} />
      {canMarketing ? <AdsSection params={params} showDelta={showDelta} /> : null}
      <MoreSection />
    </div>
  );
}

function SalesSection({ showDelta, withSources }: { showDelta: boolean; withSources: boolean }) {
  const t = useT();
  const locale = useLocale();
  const [params] = useDashboardParams();
  const overview = useOverview(params);
  const funnel = useFunnel(params, 'cohort');
  const kpis = overview.data?.data.kpis;
  const conversion = kpis?.overallConversion;

  return (
    <HomeSection id="home-sales" title={t.home.sections.sales} hint={t.home.sectionHints.sales}>
      {overview.error && !overview.data ? (
        <Async query={overview}>{() => null}</Async>
      ) : (
        <StatStrip columns={4}>
          <StatTile
            label={t.kpi.leadsNew.name}
            definition={t.kpi.leadsNew.definition}
            value={kpis ? formatNumber(kpis.leadsNew.value, locale) : undefined}
            metric={kpis?.leadsNew}
            showDelta={showDelta}
          />
          <StatTile
            label={t.kpi.dealsWon.name}
            definition={t.kpi.dealsWon.definition}
            value={kpis ? formatNumber(kpis.dealsWon.value, locale) : undefined}
            metric={kpis?.dealsWon}
            showDelta={showDelta}
          />
          <StatTile
            label={t.kpi.containersSold.name}
            definition={t.kpi.containersSold.definition}
            value={kpis ? formatNumber(kpis.containersSold.value, locale) : undefined}
            unit={t.units.containers}
            metric={kpis?.containersSold}
            showDelta={showDelta}
          />
          <StatTile
            label={t.kpi.overallConversion.name}
            definition={t.kpi.overallConversion.definition}
            value={conversion ? formatPercent(conversion.value, locale) : undefined}
            metric={conversion}
            showDelta={showDelta}
            note={
              conversion
                ? t.kpi.overallConversion.basis(
                    formatDate(conversion.basis.cohortFrom),
                    formatDate(conversion.basis.cohortTo),
                    formatNumber(conversion.basis.size, locale),
                  )
                : undefined
            }
          />
        </StatStrip>
      )}

      <div className="grid gap-4 lg:grid-cols-12">
        <Panel
          title={t.home.funnel.title}
          className={withSources ? 'min-w-0 lg:col-span-7' : 'min-w-0 lg:col-span-12'}
        >
          <p className="-mt-1 mb-4 text-sm text-muted-foreground">{t.home.funnel.subtitle}</p>
          <Async query={funnel} skeleton={<Skeleton className="h-72 w-full" />}>
            {({ data }) =>
              data.mode !== 'cohort' || data.cohortSize === 0 ? (
                <EmptyState title={t.common.noData} description={t.home.funnel.empty} />
              ) : (
                <div className="space-y-4">
                  <HomeFunnel
                    data={{
                      size: data.cohortSize,
                      contacted: data.contactedOutcomes.contacted,
                      won: data.contactedOutcomes.won,
                      lost: data.contactedOutcomes.lost,
                      open: data.contactedOutcomes.open,
                    }}
                  />
                  {data.maturity.immature ? (
                    <ImmatureCohortNotice maturity={data.maturity} />
                  ) : null}
                </div>
              )
            }
          </Async>
        </Panel>
        {withSources ? <SourcesPanel /> : null}
      </div>
    </HomeSection>
  );
}

function SourcesPanel() {
  const t = useT();
  const locale = useLocale();
  const [params] = useDashboardParams();
  const marketing = useMarketing(params);
  const search = carryOver(new URLSearchParams(window.location.search));
  return (
    <Panel
      title={t.home.sources.title}
      className="min-w-0 lg:col-span-5"
      actions={
        <Link
          to={{ pathname: '/marketing', search }}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t.home.sources.more}
        </Link>
      }
    >
      <Async query={marketing}>
        {({ data }) => {
          const total = data.totals.leadsNew;
          if (total === 0)
            return <EmptyState title={t.common.noData} description={t.home.sources.empty} />;
          const unknown = data.rows.find((r) => r.source.id === null)?.leadsNew ?? 0;
          return (
            <div className="space-y-4">
              <BarList
                ariaLabel={t.home.sources.title}
                unit={t.units.leads}
                items={data.rows.map((row) => ({
                  key: row.source.id,
                  label: row.source.name ?? t.marketing.unknownSource,
                  value: row.leadsNew,
                  note: formatPercent(share(row.leadsNew, total), locale, 0),
                }))}
              />
              {unknown > 0 ? (
                <p className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-foreground">
                  {t.home.sources.unknownShare(formatPercent(share(unknown, total), locale, 0))}
                </p>
              ) : null}
            </div>
          );
        }}
      </Async>
    </Panel>
  );
}

function CallsSection({ showDelta }: { showDelta: boolean }) {
  const t = useT();
  const locale = useLocale();
  const [params] = useDashboardParams();
  const summary = useCallSummary(params);
  const patterns = useCallPatterns(params);
  const data = summary.data?.data;
  const k = t.home.kpi;
  const seconds = (value: number | null) =>
    formatSeconds(value, locale, { seconds: t.units.seconds, minutes: t.units.minutes });

  return (
    <HomeSection id="home-calls" title={t.home.sections.calls} hint={t.home.sectionHints.calls}>
      {summary.error && !data ? (
        <Async query={summary}>{() => null}</Async>
      ) : (
        <StatStrip columns={5}>
          <StatTile
            label={k.callsTotal.name}
            definition={k.callsTotal.definition}
            value={data ? formatNumber(data.kpis.total.value, locale) : undefined}
            metric={data?.kpis.total}
            showDelta={showDelta}
            note={
              data
                ? k.callsTotal.split(
                    formatNumber(data.counts.inbound, locale),
                    formatNumber(data.counts.outbound, locale),
                  )
                : undefined
            }
          />
          <StatTile
            label={k.answerRate.name}
            definition={k.answerRate.definition}
            value={data ? formatPercent(data.kpis.answerRate.value, locale) : undefined}
            metric={data?.kpis.answerRate}
            showDelta={showDelta}
            note={
              data
                ? k.answerRate.missed(formatNumber(data.counts.inboundMissed, locale))
                : undefined
            }
          />
          <StatTile
            label={k.avgWait.name}
            definition={k.avgWait.definition}
            value={data ? seconds(data.kpis.avgWaitSeconds.value) : undefined}
            metric={data?.kpis.avgWaitSeconds}
            showDelta={showDelta}
            unavailable={
              data && !data.kpis.avgWaitSeconds.available ? k.avgWait.unavailable : undefined
            }
          />
          <StatTile
            label={k.avgTalk.name}
            definition={k.avgTalk.definition}
            value={data ? seconds(data.kpis.avgTalkSeconds.value) : undefined}
            metric={data?.kpis.avgTalkSeconds}
            showDelta={showDelta}
          />
          <StatTile
            label={k.missedNotReturned.name}
            definition={k.missedNotReturned.definition(data?.callback.windowMinutes ?? 60)}
            value={data ? formatNumber(data.kpis.missedNotReturned.value, locale) : undefined}
            metric={data?.kpis.missedNotReturned}
            showDelta={showDelta}
            note={
              data && data.callback.pending > 0
                ? k.missedNotReturned.pending(formatNumber(data.callback.pending, locale))
                : undefined
            }
          />
        </StatStrip>
      )}

      <Panel title={t.home.hours.title} className="min-w-0">
        <Async query={patterns} skeleton={<Skeleton className="h-64 w-full" />}>
          {({ data: d }) =>
            d.byHour.every((h) => h.total === 0) ? (
              <EmptyState title={t.common.noData} description={t.home.hours.empty} />
            ) : (
              <HourlyCallsChart hours={d.byHour} />
            )
          }
        </Async>
      </Panel>
      <Panel title={t.home.heatmap.title} className="min-w-0">
        <Async query={patterns} skeleton={<Skeleton className="h-64 w-full" />}>
          {({ data: d }) =>
            d.byHour.every((h) => h.total === 0) ? (
              <EmptyState title={t.common.noData} description={t.home.hours.empty} />
            ) : (
              <CallHeatmap cells={d.heatmap} />
            )
          }
        </Async>
      </Panel>
    </HomeSection>
  );
}

function TrendPanel() {
  const t = useT();
  const [params] = useDashboardParams();
  const [metric, setMetric] = useState<(typeof TREND_METRICS)[number]>('leadsNew');
  const trend = useTrend(params, [metric]);
  return (
    <Panel
      title={t.overview.trend}
      className="min-w-0 lg:col-span-12"
      actions={
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t.overview.trendMetric}</span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as typeof metric)}
            className="h-11 rounded-md border border-input bg-card px-2 lg:h-9"
          >
            {TREND_METRICS.map((m) => (
              <option key={m} value={m}>
                {t.trendMetrics[m]}
              </option>
            ))}
          </select>
        </label>
      }
    >
      <Async query={trend}>
        {({ data }) =>
          // One bucket (e.g. a single day) is one bar filling the chart: nothing to compare.
          (data.series[0]?.points.length ?? 0) < 2 ? (
            <EmptyState
              title={t.overview.trendTooShortTitle}
              description={t.overview.trendTooShort}
            />
          ) : (
            <>
              <p className="mb-2 text-xs text-muted-foreground">
                {t.granularity[data.granularity]}
              </p>
              <TrendChart
                title={t.trendMetrics[metric]}
                series={data.series.map((s) => ({
                  key: s.metric,
                  label: t.trendMetrics[s.metric],
                  points: s.points,
                }))}
              />
            </>
          )
        }
      </Async>
    </Panel>
  );
}

function MoreSection() {
  const t = useT();
  const locale = useLocale();
  const [params] = useDashboardParams();
  const overview = useOverview(params);
  return (
    <HomeSection id="home-more" title={t.home.sections.more}>
      <div className="grid gap-4 lg:grid-cols-12">
        <TrendPanel />
        <Panel title={t.overview.containersByType} className="min-w-0 lg:col-span-6">
          <Async query={overview}>
            {({ data: d }) =>
              d.containersByType.length === 0 ? (
                <EmptyState title={t.common.noData} description={t.overview.containersEmpty} />
              ) : (
                <BarList
                  ariaLabel={t.overview.containersByType}
                  unit={t.units.containers}
                  items={d.containersByType.map((row) => ({
                    key: row.typeId,
                    label: row.name ?? t.common.unknown,
                    value: row.containers,
                    note: row.isAuto ? t.overview.autoType : undefined,
                  }))}
                />
              )
            }
          </Async>
        </Panel>
        <Panel title={t.overview.alerts} className="min-w-0 lg:col-span-6">
          <Async query={overview}>
            {({ data: d }) =>
              d.alerts.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.overview.noAlerts}</p>
              ) : (
                <ul className="divide-y">
                  {d.alerts.map((alert) => (
                    <li
                      key={alert.code}
                      className="flex items-center justify-between gap-2 py-2 text-sm"
                    >
                      <span>{t.alerts[alert.code]}</span>
                      <span className="flex items-center gap-2">
                        {alert.count !== null ? (
                          <span className="font-medium tabular-nums">
                            {formatNumber(alert.count, locale)}
                          </span>
                        ) : null}
                        <Badge>{t.alerts.severity[alert.severity]}</Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )
            }
          </Async>
        </Panel>
      </div>
    </HomeSection>
  );
}
